/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import styled, { css, SimpleInterpolation } from "styled-components";
import {
  devices,
  fontFamilies,
  isMobileSize,
  useScreenSize,
} from "components/shared/Styles";
import { AppHeaderHeight } from "components/shared/AppHeader";
import backdropImage from "images/home/tile.svg";
import featuresBackdropImg from "components/shared/images/features-backdrop.jpg?responsive";
import { useCurrentUser } from "actions/CurrentUserActions";
import { HomePageButton } from "components/shared/HomePageButton";
import { WithDottedBorder } from "components/shared/DottedBorder";
import useDropWaveformBlockOrNativeFile, {
  makeSingleFileDropHandler,
} from "hooks/useDropWaveformBlockOrNativeFile";

import recordStep1Img from "components/UseCases/images/record-step1.svg";
import recordStep2Img from "components/UseCases/images/record-step2.svg";
import recordStep3Img from "components/UseCases/images/record-step3.svg";
import { match, P } from "ts-pattern";
import { CurrentUserLoadingState } from "reducers/CurrentUserReducer";

export const UseCaseContent = styled.div`
  font-family: ${fontFamilies};
  overflow-x: hidden;
  background-color: var(--spectrum-global-color-static-white);
  position: relative;
`;

export const UseCaseColumnContainer = styled.div`
  max-width: 1252px;
  margin: auto;
  padding: ${AppHeaderHeight} 24px 0 24px;

  a {
    color: var(--spectrum-indigo-900);
  }

  a:visited {
    color: var(--spectrum-indigo-900);
    text-decoration-color: var(--spectrum-indigo-900);
  }

  ${devices.small} {
    padding: ${AppHeaderHeight} 16px 0 16px;
  }
`;

export const UseCaseBackdrop = styled.div`
  position: absolute;
  width: 100%;
  height: 750px;
  top: ${AppHeaderHeight};
  left: 0;
  background-color: var(--spectrum-indigo-100);
  background-image: url(${backdropImage});
  background-position: center center;
  gap: 48px;
`;

export const UseCaseColumn: React.FC = (props) => {
  return (
    <UseCaseColumnContainer>
      <UseCaseBackdrop />
      {props.children}
    </UseCaseColumnContainer>
  );
};

const ActionImage = styled.img`
  margin-top: 24px;
`;

interface ActionContainerProps {
  image: string;
  title: string;
  actionButton: React.ReactNode;
}

export const ActionContainer: React.FC<ActionContainerProps> = (props) => {
  return (
    <>
      <ActionImage src={props.image} alt={props.title} />
      <GetStartedSpan>{props.title}</GetStartedSpan>
      {props.actionButton}
    </>
  );
};

const MainCTAContainer = styled.div`
  position: relative;
  height: 523px;
  background: var(--spectrum-global-color-static-white);
  box-shadow: var(--phonos-box-shadow);
  border-radius: 64px;
  padding: 16px;
  overflow: hidden;
  margin-top: 32px;

  ${devices.small} {
    padding: 30px;
    border-radius: 32px;
    height: 100%;
  }
`;

const MainDottedBorderWrapper = styled(WithDottedBorder)<{
  backgroundcolor: string;
}>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  height: 100%;
  border-radius: 48px;
  background-color: ${({ backgroundcolor }): string => backgroundcolor};

  ${devices.small} {
    flex-direction: column;
    justify-content: flex-end;
    flex-direction: column-reverse;
    gap: 16px;
  }
`;

const ActionColumn = styled.div<{
  highlighted?: boolean;
  mobileHeight?: number;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-radius: 48px;
  gap: 24px;
  align-items: center;
  transition: background-color 350ms ease-in-out;

  ${devices.large} {
    padding: 40px;
    width: 50%;
  }

  ${devices.small} {
    height: ${({ mobileHeight }): string =>
      mobileHeight ? `${mobileHeight}px` : "50%"};
    border-radius: 20px;
    padding: 8px;
    gap: 24px;
  }

  ${({ highlighted }): SimpleInterpolation => {
    if (highlighted) {
      return css`
        background-color: rgba(237, 238, 255, 1);
      `;
    }
  }})
`;

const ActionTitle = styled.h1`
  width: 100%;
  letter-spacing: -0.005em;
  font-size: 42px;
  line-height: 45px;
  font-weight: 900;

  ${devices.medium} {
    font-size: 52px;
    line-height: 57px;
  }

  ${devices.large} {
    font-size: 60px;
    line-height: 66px;
  }

  background: linear-gradient(133.13deg, #5266ff 5.68%, #b871ff 96.33%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const ActionBody = styled.div`
  color: var(--spectrum-gray-900);
  font-size: 18px;
  font-weight: 500;
  line-height: 23px;
`;

export const UseCaseTagline = styled.h2`
  position: relative;
  color: var(--spectrum-global-color-static-black);
  font-size: 36px;
  font-weight: 900;
  text-align: center;

  ${devices.small} {
    font-size: 22px;
    margin: 8px 32px 32px 32px;
  }
`;

const LoginSpan = styled.span`
  font-size: 17px;
  z-index: 1;
`;

const GetStartedSpan = styled.span`
  font-size: 22px;
  font-weight: 900;
  line-height: 28.6px;
  text-align: center;
`;

const SignUpButtonContainer = styled(HomePageButton)`
  width: 170px;
  height: 56px;
  border-radius: 28px;
  justify-content: center;
  font-size: 17px;
`;

export const SignUpButton: React.FC = () => {
  const onSignUp = useCallback(() => {
    window.adobeIMS?.signUp();
  }, []);

  return (
    <SignUpButtonContainer onClick={onSignUp}>
      <span>{I18n.t("enhance_speech.sign_up")}</span>
    </SignUpButtonContainer>
  );
};

export const CTALogin: React.FC = () => {
  const signIn = useCallback(() => {
    window.adobeIMS?.signIn();
  }, []);
  return (
    <>
      <SignUpButton />
      <LoginSpan>
        {`${I18n.t("enhance_speech.already_have_account_instructions")} `}
        <a href="#" onClick={signIn}>
          {I18n.t("enhance_speech.sign_in")}
        </a>
      </LoginSpan>
    </>
  );
};

const TopCTALogin: React.FC = () => {
  return (
    <>
      <GetStartedSpan>{I18n.t("use_cases.get_started")}</GetStartedSpan>
      <LoginSpan>{I18n.t("use_cases.sign_up_for_free")}</LoginSpan>
      <CTALogin />
    </>
  );
};

interface MainCTAProps {
  title: string;
  body: string;
  actionContainer: React.ReactNode;
  heroImageSrcSet?: string;
  handleDroppedFile?: (file: File) => Promise<void>;
  acceptedContentTypes?: string[];
}

const MainCTAImage = styled.img`
  max-width: 100%;
  filter: drop-shadow(0px 12px 24px rgba(0, 0, 0, 0.16));
`;

export const MainCTA: React.FC<MainCTAProps> = ({
  title,
  body,
  actionContainer,
  heroImageSrcSet,
  handleDroppedFile,
  acceptedContentTypes,
}: MainCTAProps) => {
  const { currentUser, currentUserLoadingState } = useCurrentUser();
  const screenSize = useScreenSize();
  const showDropBorder = useMemo(() => {
    return (
      Boolean(screenSize) && //Force re-render on screen size change
      !isMobileSize() &&
      currentUserLoadingState === CurrentUserLoadingState.LOADED &&
      Boolean(currentUser) &&
      Boolean(handleDroppedFile)
    );
  }, [screenSize, currentUserLoadingState, currentUser, handleDroppedFile]);

  const onFileDroppedInside = makeSingleFileDropHandler(
    useCallback(
      (droppedFile: File) => {
        handleDroppedFile?.(droppedFile);
      },
      [handleDroppedFile]
    )
  );

  const { isDraggingOver, dropRef } = useDropWaveformBlockOrNativeFile({
    onFileDroppedHandler: onFileDroppedInside,
    acceptedContentTypes: acceptedContentTypes,
  });

  const backgroundColor = useMemo(() => {
    return showDropBorder && isDraggingOver
      ? "rgba(var(--spectrum-global-color-static-indigo-600-rgb), 0.16)"
      : "var(--spectrum-global-color-static-white)";
  }, [isDraggingOver, showDropBorder]);

  return (
    <MainCTAContainer className="main-cta-container">
      <MainDottedBorderWrapper
        color={"#5C5CE0"}
        dashlength={8}
        radius={48}
        ref={dropRef}
        backgroundcolor={backgroundColor}
        borderStyle={{
          opacity: showDropBorder ? 1 : 0,
          transition: "opacity 500ms ease-in-out",
        }}
        className="main-cta-dotted-border-wrapper"
      >
        <ActionColumn>
          {heroImageSrcSet && (
            <MainCTAImage srcSet={heroImageSrcSet} alt={title} />
          )}
          <ActionTitle>{title}</ActionTitle>
          <ActionBody>{body}</ActionBody>
        </ActionColumn>
        <ActionColumn highlighted={!currentUser} mobileHeight={244}>
          {match([currentUser, currentUserLoadingState])
            .with([P.any, P.not(CurrentUserLoadingState.LOADED)], () => (
              <sp-progress-circle size="l" indeterminate />
            ))
            .with([P.nullish, CurrentUserLoadingState.LOADED], () => (
              <TopCTALogin />
            ))
            .otherwise(() => actionContainer)}
        </ActionColumn>
      </MainDottedBorderWrapper>
    </MainCTAContainer>
  );
};

const FeaturesContainer = styled.div`
  position: relative;
  background: var(--spectrum-global-color-static-white);
  border-radius: 64px;
  padding: 48px 48px 0 48px;
  margin-top: 48px;
  gap: 48px;
  overflow: hidden;

  ${devices.small} {
    margin-top: 32px;
    border-radius: 48px;
    flex-direction: column;
    padding: 24px;
    gap: 24px;
  }
`;

const FeaturesBackdrop = styled.img.attrs({
  srcSet: featuresBackdropImg.srcSet,
})`
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
  aspect-ratio: ${featuresBackdropImg.width / featuresBackdropImg.height};
`;

interface UseCaseContainerProps {
  className?: string;
}

export const UseCaseContainer: React.FC<UseCaseContainerProps> = (props) => {
  const backdropRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    // this is a workaround because our version of react doesn't recognize fetchPriority as an attribute
    backdropRef.current!.fetchPriority = "high";
  }, []);

  return (
    <FeaturesContainer className={props.className}>
      <FeaturesBackdrop ref={backdropRef} />
      {props.children}
    </FeaturesContainer>
  );
};

const StepsContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  gap: 32px;
  margin-top: 48px;
  justify-content: space-between;

  ${devices.small} {
    flex-direction: column;
    gap: 16px;
    margin-top: 16px;
  }
`;

interface StepsProps {
  stepPrefix: string;
}

export const Steps: React.FC<StepsProps> = (props) => {
  return (
    <StepsContainer>
      <Step
        image={recordStep1Img}
        title={I18n.t(`use_cases.${props.stepPrefix}.step1.title`)}
        body={I18n.t(`use_cases.${props.stepPrefix}.step1.description`)}
      />
      <Step
        image={recordStep2Img}
        title={I18n.t(`use_cases.${props.stepPrefix}.step2.title`)}
        body={I18n.t(`use_cases.${props.stepPrefix}.step2.description`)}
      />
      <Step
        image={recordStep3Img}
        title={I18n.t(`use_cases.${props.stepPrefix}.step3.title`)}
        body={I18n.t(`use_cases.${props.stepPrefix}.step3.description`)}
      />
    </StepsContainer>
  );
};

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 359px;
  height: 233px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.2);
  box-shadow: var(--phonos-box-shadow);

  ${devices.small} {
    width: 100%;
  }
`;

const StepImage = styled.img`
  width: 56px;
  height: 56px;
  margin-bottom: 16px;
`;

const StepTitle = styled.div`
  font-size: 22px;
  font-weight: 800;
  text-align: center;
`;

const StepBody = styled.div`
  font-size: 18px;
  font-weight: 500;
  line-height: 23px;
  width: 60%;
  text-align: center;
  margin-top: 4px;
  text-wrap: balance;
`;

interface StepProps {
  image: string;
  title: string;
  body: string;
}

const Step: React.FC<StepProps> = (props) => {
  return (
    <StepContainer>
      <StepImage src={props.image} />
      <StepTitle>{props.title}</StepTitle>
      <StepBody>{props.body}</StepBody>
    </StepContainer>
  );
};

export interface FeatureBulletPoint {
  text: string;
  icon: string;
}

type UseCaseFeatureProps = {
  title: string;
  body: string;
  bulletPoints: FeatureBulletPoint[];
} & ({ image1x: string; image2x: string } | { srcSet: string });

const FeatureContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 72px 0;
  gap: 48px;

  ${devices.large} {
    &:nth-child(even) {
      flex-direction: row-reverse;
    }
  }

  ${devices.small} {
    flex-direction: column;
    gap: 24px;
    margin: 48px 0;
  }
`;

const FeatureImage = styled.img`
  border-radius: 32px;
  width: 50%;
  object-fit: cover;
  align-self: center;

  ${devices.small} {
    width: 100%;
    max-height: 324px;
    object-fit: cover;
  }
`;

const FeatureDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${devices.large} {
    width: 50%;
  }
`;

const FeatureTitle = styled.div`
  font-size: 40px;
  font-weight: 900;
  line-height: 44px;
  color: var(--spectrum-global-color-static-black);

  ${devices.small} {
    font: normal normal 900 34px/40px ${fontFamilies};
  }
`;

const FeatureBody = styled.div`
  font-size: 22px;
  font-weight: 400;
  line-height: 28px;
  color: var(--spectrum-gray-900);

  ${devices.large} {
    width: 90%;
  }
`;

export const BulletPoints = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: flex-start;
  flex-grow: 2;
  width: 90%;

  ${devices.small} {
    width: 100%;
    gap: 12px;
  }
`;

const BulletPoint = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 6px;
  line-height: 20px;
  font-size: 16px;
`;

const BulletImage = styled.img`
  margin-top: 6px;
  width: 18px;
  height: 18px;
`;

const BulletText = styled.span`
  font-size: 22px;
  font-weight: 700;
  line-height: 29px;
`;

export const UseCaseFeature: React.FC<UseCaseFeatureProps> = (props) => {
  return (
    <FeatureContainer>
      <FeatureImage
        srcSet={
          "srcSet" in props
            ? props.srcSet
            : `${props.image1x} 1x, ${props.image2x} 2x`
        }
        alt={props.title}
      />
      <FeatureDescription>
        <FeatureTitle>{props.title}</FeatureTitle>
        <FeatureBody>{props.body}</FeatureBody>
        <BulletPoints>
          {props.bulletPoints.map((featureBulletPoint, index) => (
            <BulletPoint key={featureBulletPoint.text}>
              <BulletImage src={featureBulletPoint.icon} />
              <BulletText>{featureBulletPoint.text}</BulletText>
            </BulletPoint>
          ))}
        </BulletPoints>
      </FeatureDescription>
    </FeatureContainer>
  );
};

const BottomActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  margin: 0 32px 56px 32px;

  ${devices.small} {
    margin-bottom: 84px;
  }
`;

const BottomActionText = styled.h3`
  font-size: 22px;
  font-weight: 400;
  line-height: 28px;
  text-align: center;
`;

const BottomActionTitle = styled.h2`
  font-size: 36px;
  font-weight: 900;
  line-height: 46px;
  text-align: center;
`;

interface BottomCTAProps {
  title: string;
  subtitle: string;
  actionButton: React.ReactNode;
}

export const BottomCTA: React.FC<BottomCTAProps> = (props) => {
  const { currentUser } = useCurrentUser();

  return (
    <BottomActionContainer>
      <BottomActionText>{props.subtitle}</BottomActionText>
      <BottomActionTitle>{props.title}</BottomActionTitle>
      {currentUser ? props.actionButton : <CTALogin />}
    </BottomActionContainer>
  );
};
